import React, { useState, useEffect, useCallback } from "react";
import "./App.css";
import { createCheckoutSession, analyzeMarket, fetchProfiles, runCompetitorCheck } from "./api/marketIntelligence";
import TermsOfService from "./TermsOfService";

const PENDING_KEY = "market_intelligence_pending";
const LAST_REPORT_KEY = "market_intelligence_last_report";

// ── Sample report (hardcoded) ─────────────────────────────────────
const SAMPLE_QUICK_TEXT = `MARKET SIGNAL: MODERATE OPPORTUNITY — 61/100

OPPORTUNITY SNAPSHOT
- Competitors: 45 (High density)
- Avg rating: 4.5 → you must exceed 4.8 to compete
- Demand signal: 35,685+ reviews in market
- Key gap: No high-end ($$$+) coffee experience in the market
- Entrenchment: 22 competitors with 400+ reviews (hard to displace)
- Real competition: 45 active operators (0 have zero reviews)
- Growth signal: 0 chains/franchises present (independent market with proven organic demand)
- Market spread: 15 geographic clusters (high micro-market opportunity)

MARKET GAPS
- The $$$$ price tier is completely unoccupied — no operator is running a premium experiential coffee concept, which leaves the high-income Austin demographic without a natural destination.
- With 22 entrenched independents and zero chains, the market rewards loyalty but punishes sameness — operators without a distinct cultural identity bleed customers to already-loved locals.
- 15 geographic clusters signal that several Austin neighborhoods are running their own micro-markets, meaning a well-placed entry can own a node without fighting the top-rated downtown operators head-on.

TOP COMPETITORS
- Terrible Love | 4.9 stars | 375 reviews — wins on atmosphere and word-of-mouth velocity, likely built a cult following through aesthetic consistency and social media identity, not just coffee quality.
- Flora Coffee and Culture | 4.9 stars | 339 reviews — the "culture" positioning signals community programming that drives repeat visits beyond the cup, making it sticky in ways a standard shop cannot match.
- Trippy Buck Coffee | 5 stars | 20 reviews — too early to call a threat, but a perfect score out of the gate means a concept with real differentiation that could entrench fast.

WHAT YOU SHOULD DO
- Enter the $$$$ tier with a defined concept — think reservation-style tastings, rare single-origins, or a bar-forward experience — and price accordingly to own the gap.
- Target a cluster neighborhood where entrenched competitors are thin; avoid direct proximity to Terrible Love or Flora without a clear counter-positioning.
- Build your Google Business Profile and Instagram presence before you open — in a 4.5-average market, your first 50 reviews will define your trajectory permanently.
- Partner with Austin food and lifestyle micro-influencers at launch, not after — organic credibility in this market is earned early or not at all.

FINAL TAKE
This market is saturated at the middle but genuinely open at the top. You should only enter if you have a specific premium concept, real capital to execute it, and the patience to build a loyal base against 22 already-loved operators. If you enter as another solid neighborhood coffee shop with good beans and nice vibes, you will be invisible.

WHERE TO LOOK
- The East Austin corridor carries strong foot traffic and a demographic that spends on experience over convenience, with cluster data suggesting room for a premium entrant → see full report for breakdown.
- South Congress adjacent blocks show independent coffee density but no $$$$ operator, making it a logical landing zone for an upscale concept targeting the tourist and creative class crossover → see full report for breakdown.
- North Loop has emerging residential density with fewer entrenched competitors than central clusters, offering a lower-resistance entry point for a neighborhood-anchored premium shop → see full report for breakdown.

---
Generated: April 16, 2026
Data sources: Google Places API, Foursquare
Powered by Small Business AI Platform`;

const SAMPLE_FULL_TEXT = `EXECUTIVE SUMMARY

Austin's coffee market is saturated at 45 identified competitors with an average rating of 4.5, meaning quality is the baseline expectation, not a differentiator. The top performers — Trippy Buck Coffee (5.0), Flora Coffee & Culture (4.9), and Terrible Love (4.9) — are setting a high bar through distinct identity and community connection, not just good espresso. However, with 15 geographic clusters identified and the highest-density areas showing low internal competition, real white space exists for a well-positioned entrant. Success here will depend on location precision, a clear concept, and a pricing or experience angle the market hasn't fully claimed.


MARKET LANDSCAPE

Austin's coffee scene is mature, independent-heavy, and personality-driven. The dominant players are not chains — they are neighborhood institutions with strong brand identities. Trippy Buck on Brazos Street owns the downtown professional corridor. Flat Track, Figure 8, and Fleet Coffee have locked up the East Austin creative demographic with affordable, approachable pricing. The South Congress corridor is anchored by Mañana, and the Burnet Road area has Neon Belly Coffee & Bakery holding strong at 4.8.

What stands out is the clustering of high-rated, low-to-mid priced shops ($ and $$) across the most trafficked corridors. Nearly every top competitor is priced at $ or $$, and several have no listed price at all. There is virtually no representation at the premium end of the market. The competitive pressure is real, but it is concentrated in specific corridors and specific price bands.


KEY OPPORTUNITIES

1. Premium Experience Gap ($$$-$$$$)
The data flags a pricing gap at the top tier. Every top-rated competitor is operating at $ or $$ price points. No one is owning the elevated, experiential coffee concept — think curated single-origins, tableside brewing, food pairings, or a hospitality-forward model that competes more with wine bars than with drip coffee shops. Austin's population includes a significant base of high-income professionals and transplants from coastal cities who are accustomed to paying for premium experiences. This lane is open.

2. Underserved Geographic Clusters
With 15 clusters identified and the highest-density areas showing low competition, there are specific neighborhoods where foot traffic exists but coffee infrastructure hasn't caught up. A new entrant who plants a flag in one of these emerging corridors before the market fills in has a meaningful first-mover advantage.

3. Concept Differentiation in a Crowded Middle
The mid-market ($ to $$) is crowded but not invincible. The opportunity is not to out-coffee the competition but to layer in a secondary draw — a serious food program, a retail component, a cultural or community programming angle — that makes the shop a destination rather than a stop.


COMPETITIVE THREATS

The real threats are not the average players — they are the top-tier independents with loyal followings. Trippy Buck, Flora Coffee & Culture, and Terrible Love represent the kind of community-embedded businesses that are very difficult to displace once established. East Austin is particularly fortified, with Flat Track, Figure 8, and Fleet Coffee forming a de facto cluster of trusted, affordable options that have captured the neighborhood's identity.

Any new entrant opening near these operators without a meaningfully different concept will struggle to pull customers. Austin coffee drinkers are loyal and opinionated. A generic or derivative concept will not survive proximity to these shops.


STRATEGIC RECOMMENDATIONS

1. Choose a neighborhood before choosing a concept. Location data shows real variance in competitive density. Identify one of the lower-competition clusters and build your concept around that community's specific demographics and gaps.

2. Pursue the premium lane deliberately. If the business model can support it, a $$$-tier concept with a strong food program and intentional design is the most defensible position in this market right now. No one is doing it at scale.

3. Build a visible identity before opening. Austin's top performers have strong brand personalities. Invest in concept development, naming, and visual identity early — not as an afterthought to operations.

4. Avoid direct overlap with East Austin's established cluster. Flat Track, Figure 8, and Fleet Coffee have that corridor locked. Opening nearby without a radically different angle is a losing bet.

5. Prioritize Google presence and ratings from day one. With an average market rating of 4.5, anything below 4.6 will read as below average. A structured review strategy from the first week of operation is not optional — it is competitive infrastructure.


WHERE TO OPEN

1. North Burnet / The Domain Corridor
This area is seeing significant residential and commercial growth. The broader corridor has room for additional concepts, particularly anything targeting the Domain's tech-worker and young professional demographic. Foot traffic anchors include retail, fitness studios, and office parks. The risk is that rising commercial rents in this corridor can compress margins quickly.
Analyze an address in North Burnet / The Domain Corridor →

2. South Lamar / Bouldin Creek
South Lamar has strong pedestrian culture, a design-forward demographic, and a gap at the premium end of the coffee market. The neighborhood skews toward higher disposable income and values aesthetics and experience — a natural fit for a concept pushing into the $$$ tier. The risk is high visibility cuts both ways; a weak concept will be publicly ignored.
Analyze an address in South Lamar / Bouldin Creek →

3. Mueller / East 51st Corridor
Mueller is a planned urban neighborhood with a built-in, walkable customer base, young families, and professionals who work from home. Coffee shop density here is lower than in East Austin proper, and the demographic is underserved by the current independent scene. The risk is that Mueller's community can be insular — local operators who embed early earn loyalty, but outsiders perceived as opportunistic face resistance.
Analyze an address in Mueller / East 51st Corridor →

---
Generated: April 16, 2026
Data sources: Google Places API, Foursquare
Analysis powered by Small Business AI Platform`;

const SAMPLE_BUSINESSES = [
  { name: "Flora Coffee & Culture", rating: 4.9, review_count: 339, price_level: "Not listed", categories: ["coffee_shop", "cafe"] },
  { name: "Mozart's Coffee Roasters", rating: 4.5, review_count: 10497, price_level: "$$", categories: ["coffee_shop", "cafe", "live_music_venue"] },
  { name: "Cosmic Pickle", rating: 4.7, review_count: 4303, price_level: "$$", categories: ["coffee_shop", "cafe", "bar"] },
  { name: "Epoch Coffee", rating: 4.5, review_count: 2465, price_level: "$", categories: ["coffee_shop", "cafe"] },
  { name: "The Brew and Brew", rating: 4.5, review_count: 1156, price_level: "$", categories: ["coffee_shop", "cafe", "bar"] },
  { name: "Flat Track Coffee", rating: 4.7, review_count: 853, price_level: "$", categories: ["cafe", "bicycle_store"] },
  { name: "Neon Belly Coffee & Bakery", rating: 4.8, review_count: 125, price_level: "Not listed", categories: ["coffee_shop", "bakery"] },
  { name: "Figure 8 Coffee Purveyors", rating: 4.7, review_count: 762, price_level: "$", categories: ["coffee_shop", "cafe"] },
  { name: "Houndstooth Coffee", rating: 4.5, review_count: 1178, price_level: "$$", categories: ["coffee_shop", "cafe"] },
  { name: "Trippy Buck Coffee", rating: 5, review_count: 20, price_level: "Not listed", categories: ["coffee_shop", "cafe"] },
  { name: "Terrible Love", rating: 4.9, review_count: 375, price_level: "Not listed", categories: ["coffee_shop", "cafe"] },
  { name: "Barrett's Coffee", rating: 4.6, review_count: 900, price_level: "$", categories: ["coffee_shop", "cafe"] },
  { name: "Lucky Lab Coffee Co.", rating: 4.4, review_count: 616, price_level: "$", categories: ["coffee_shop", "cafe"] },
  { name: "Idlewild Coffee", rating: 4.8, review_count: 280, price_level: "$", categories: ["coffee_shop", "coffee_roastery"] },
  { name: "Afuga Coffee", rating: 4.8, review_count: 670, price_level: "$$", categories: ["coffee_shop", "cafe"] },
  { name: "Mañana Coffee", rating: 4.2, review_count: 232, price_level: "$", categories: ["coffee_shop", "cafe"] },
  { name: "Jo's Coffee", rating: 4.5, review_count: 788, price_level: "$", categories: ["Coffee Shop", "American Restaurant"] },
  { name: "Halcyon", rating: 4.2, review_count: 782, price_level: "$", categories: ["Coffee Shop", "Lounge"] },
  { name: "Cenote", rating: 4.3, review_count: 680, price_level: "$", categories: ["Café", "Coffee Shop"] },
  { name: "Summer Moon Coffee Bar", rating: 4.2, review_count: 390, price_level: "$", categories: ["Café", "Coffee Shop"] },
  { name: "Radio Coffee & Beer", rating: 4.5, review_count: 456, price_level: "$$", categories: ["Coffee Shop", "Bar"] },
  { name: "Fleet Coffee Co.", rating: 4.7, review_count: 171, price_level: "$", categories: ["Coffee Shop"] },
];

const SAMPLE_QUICK_REPORT = {
  _isSample: true,
  report_type: "quick",
  analysis: { quick_report: SAMPLE_QUICK_TEXT },
};

const SAMPLE_REPORT = {
  _isSample: true,
  report_type: "full",
  analysis: {
    quick_report: SAMPLE_QUICK_TEXT,
    comprehensive_report: SAMPLE_FULL_TEXT,
    raw_data: {
      businesses: SAMPLE_BUSINESSES,
      competitive_analysis: {
        market_overview: {
          total_competitors: 45,
          average_rating: 4.5,
          total_reviews: 35685,
          competition_level: "High",
          market_quality: "Premium",
        },
        market_leaders: [
          { name: "Trippy Buck Coffee", rating: 5, review_count: 20, price_level: "Not listed" },
          { name: "Terrible Love", rating: 4.9, review_count: 375, price_level: "Not listed" },
          { name: "Flora Coffee & Culture", rating: 4.9, review_count: 339, price_level: "Not listed" },
          { name: "Neon Belly Coffee & Bakery", rating: 4.8, review_count: 125, price_level: "Not listed" },
          { name: "Afuga Coffee", rating: 4.8, review_count: 670, price_level: "$$" },
        ],
      },
      opportunities: {
        identified_opportunities: [
          { type: "Pricing Gap", priority: "High", description: "No operator in the $$$-$$$$ tier — premium experiential coffee concept is completely unoccupied." },
          { type: "Geographic Clusters", priority: "High", description: "15 clusters identified with low internal competition — first-mover advantage available in emerging corridors." },
          { type: "Concept Differentiation", priority: "Medium", description: "Mid-market is crowded but beatable with a strong secondary draw — food program, retail, or community programming." },
        ],
      },
    },
    summary_metrics: {
      total_businesses_analyzed: 45,
      opportunities_identified: 3,
    },
  },
};

// ── Parsing utilities ─────────────────────────────────────────────
function parseReport(text) {
  if (!text) return null;
  const lines = text.split("\n");
  const result = { verdict: null, score: null, sections: [], footer: [] };

  const verdictLine = lines.find(l => l.startsWith("MARKET SIGNAL:"));
  if (verdictLine) {
    const m = verdictLine.match(/MARKET SIGNAL:\s*(STRONG OPPORTUNITY|MODERATE OPPORTUNITY|HIGH COMPETITION)\s*[—-]\s*(\d+)\/100/i);
    if (m) { result.verdict = m[1].toUpperCase(); result.score = parseInt(m[2]); }
  }

  let current = null;
  for (const raw of lines) {
    const line = raw.trim();
    if (!line || line.startsWith("MARKET SIGNAL:") || line.startsWith("---")) continue;
    if (["Generated:", "Data sources:", "Powered by", "Analysis powered"].some(p => line.startsWith(p))) {
      result.footer.push(line); continue;
    }
    // Numbered section header: "1. EXECUTIVE SUMMARY"
    const isNumberedHeader = /^\d+\.\s+[A-Z][A-Z\s/-]+$/.test(line);
    // All-caps section header (no leading digit): "FINAL TAKE", "WHERE TO OPEN"
    const isAllCapsHeader = !isNumberedHeader && line === line.toUpperCase() && line.length > 2
      && !line.startsWith("-") && !/^\d/.test(line) && !line.includes("→") && !line.includes("·");
    if (isNumberedHeader || isAllCapsHeader) {
      current = { title: line, bullets: [], paragraphs: [] };
      result.sections.push(current);
    } else if (current) {
      if (line.startsWith("- ")) {
        current.bullets.push(line.slice(2));
      } else {
        // Sub-heading: "Opportunity 1: ...", "Recommendation 2: ...", "Area 1: ..."
        const isSub = /^[A-Z][a-z]+ \d+:/.test(line);
        current.paragraphs.push({ type: isSub ? 'sub' : 'p', text: line });
      }
    }
  }
  return result;
}

function verdictCfg(verdict) {
  const v = (verdict || "").toUpperCase();
  if (v === "STRONG OPPORTUNITY") return { bg: "#15803d", text: "#fff", glow: "rgba(21,128,61,0.2)" };
  if (v === "HIGH COMPETITION")   return { bg: "#dc2626", text: "#fff", glow: "rgba(220,38,38,0.2)" };
  if (v === "MODERATE OPPORTUNITY") return { bg: "#d97706", text: "#fff", glow: "rgba(217,119,6,0.2)" };
  return { bg: "#6b7280", text: "#fff", glow: "rgba(107,114,128,0.2)" };
}

function ratingBg(r) {
  if (!r) return "#f3f4f6";
  if (r >= 4.5) return "#dcfce7";
  if (r >= 4.0) return "#fef9c3";
  if (r >= 3.5) return "#fed7aa";
  return "#fee2e2";
}

function copyText(text) {
  navigator.clipboard.writeText(text).catch(() => {});
}


// ── App ───────────────────────────────────────────────────────────
export default function App() {
  const [status, setStatus] = useState("idle");
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [biz, setBiz] = useState("");
  const [loc, setLoc] = useState("");
  const [pendingSid, setPendingSid] = useState(null);
  const [ctx, setCtx] = useState("");
  const [addr, setAddr] = useState("");
  const [rType, setRType] = useState("quick");
  const [errs, setErrs] = useState({});
  const [copied, setCopied] = useState(false);

  const runAnalysis = useCallback(async (payload, sessionId) => {
    setStatus("loading"); setReport(null); setError(null);
    try {
      const result = await analyzeMarket(payload, sessionId);
      setReport({ ...result, report_type: payload.report_type });
      setStatus("success");
    } catch (e) {
      setError(e.message || "Analysis failed."); setStatus("error");
    }
  }, []);

  const runCheck = useCallback(async (payload, sessionId) => {
    setStatus("loading"); setReport(null); setError(null);
    try {
      const result = await runCompetitorCheck(payload, sessionId);
      setReport({ ...result, report_type: "competitor_check" });
      setStatus("success");
    } catch (e) {
      setError(e.message || "Competitor check failed."); setStatus("error");
    }
  }, []);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);

    // Sample preview mode
    const sample = p.get("sample");
    if (sample === "quick") {
      setReport(SAMPLE_QUICK_REPORT); setStatus("success"); return;
    }
    if (sample === "full") {
      setReport(SAMPLE_REPORT); setStatus("success"); return;
    }

    // Post-Stripe return
    const sid = p.get("session_id");
    if (!sid) return;

    // Primary: restore payload from localStorage
    // Fallback: read from URL params (Stripe passes through query params on Payment Links)
    const saved = localStorage.getItem(PENDING_KEY);
    let payload;
    if (saved) {
      payload = JSON.parse(saved);
    } else {
      const bt = p.get("bt");
      const loc = p.get("loc");
      if (bt && loc) {
        payload = { business_type: bt, location: loc, report_type: p.get("rt") || "full", center_address: p.get("ca") || undefined };
      } else {
        // Can't recover automatically — show recovery form
        window.history.replaceState({}, "", window.location.pathname);
        setPendingSid(sid);
        setStatus("recover");
        return;
      }
    }
    localStorage.removeItem(PENDING_KEY);
    window.history.replaceState({}, "", window.location.pathname);
    setBiz(payload.business_type); setLoc(payload.location);
    if (payload.center_address) setAddr(payload.center_address);
    const rTypeRestored = payload.report_type || "full";
    setRType(rTypeRestored);

    if (rTypeRestored === "competitor_check") {
      runCheck(payload, sid);
      return;
    }

    if (rTypeRestored === "profiles") {
      // Restore saved report immediately, then fetch profiles-only
      const savedReport = localStorage.getItem(LAST_REPORT_KEY);
      if (savedReport) {
        const { report: restoredReport, biz: savedBiz, loc: savedLoc } = JSON.parse(savedReport);
        localStorage.removeItem(LAST_REPORT_KEY);
        setBiz(savedBiz); setLoc(savedLoc);
        setReport(restoredReport); setStatus("success");
        // Now fetch just the profiles and merge them in
        fetchProfiles({ business_type: savedBiz, location: savedLoc }, sid)
          .then(result => {
            const profiles = result?.analysis?.competitor_profiles;
            if (profiles) {
              setReport(prev => ({
                ...prev,
                report_type: "profiles",
                analysis: { ...prev.analysis, competitor_profiles: profiles },
              }));
            }
          })
          .catch(e => setError(e.message || "Profiles fetch failed."));
      } else {
        // No saved report — fall back to full re-run
        runAnalysis({ ...payload, report_type: "full" }, sid);
      }
      return;
    }

    runAnalysis(payload, sid);
  }, [runAnalysis, runCheck]);

  async function handleSubmit(e) {
    e.preventDefault();
    const v = {};
    if (!biz.trim()) v.biz = "Required";
    if (!loc.trim()) v.loc = "Required";
    if (Object.keys(v).length) { setErrs(v); return; }
    setErrs({}); setStatus("loading"); setError(null);
    const payload = { business_type: biz.trim(), location: loc.trim(), additional_context: ctx.trim(), report_type: rType, center_address: addr.trim() || undefined };
    try {
      localStorage.setItem(PENDING_KEY, JSON.stringify(payload));
      await createCheckoutSession(rType, payload);
    } catch (e) {
      localStorage.removeItem(PENDING_KEY);
      setError(e.message || "Checkout failed."); setStatus("error");
    }
  }

  async function handleUpsell() {
    const payload = { business_type: biz.trim(), location: loc.trim(), additional_context: ctx.trim(), report_type: "full" };
    setStatus("loading"); setReport(null); setError(null);
    try {
      localStorage.setItem(PENDING_KEY, JSON.stringify(payload));
      window.location.href = await createCheckoutSession("full", payload);
    } catch (e) {
      localStorage.removeItem(PENDING_KEY);
      setError(e.message || "Checkout failed."); setStatus("error");
    }
  }

  async function handleProfilesUpsell() {
    const payload = { business_type: biz.trim(), location: loc.trim(), additional_context: ctx.trim(), report_type: "profiles" };
    try {
      // Save the current report so it can be restored after Stripe redirect
      if (report) localStorage.setItem(LAST_REPORT_KEY, JSON.stringify({ report, biz: biz.trim(), loc: loc.trim() }));
      localStorage.setItem(PENDING_KEY, JSON.stringify(payload));
      window.location.href = await createCheckoutSession("profiles", payload);
    } catch (e) {
      localStorage.removeItem(PENDING_KEY);
      setError(e.message || "Checkout failed."); setStatus("error");
    }
  }

  function handleCopy(text) {
    copyText(text); setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function handlePrint() {
    window.print();
  }

  function reset() { setStatus("idle"); setReport(null); setError(null); setPendingSid(null); }

  function loadSample(type) {
    if (type === "full") {
      window.open("/reports/full_commcleaning_dallas.pdf", "_blank");
    } else if (type === "competitor_check") {
      window.open("/reports/compcheck_empanadas_SJC.pdf", "_blank");
    } else {
      window.open(`?sample=${type}`, "_blank");
    }
  }

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-inner">
          <div className="wordmark">
            <span className="wm-brand">Market Intelligence</span>
          </div>
          <p className="header-sub">Know your market before you move</p>
        </div>
      </header>

      <main className="app-main">
        {status === "idle" && (
          <InputView rType={rType} setRType={setRType} biz={biz} setBiz={setBiz}
            loc={loc} setLoc={setLoc} ctx={ctx} setCtx={setCtx}
            addr={addr} setAddr={setAddr}
            errs={errs} onSubmit={handleSubmit} onSample={loadSample} />
        )}
        {status === "loading" && <LoadingView />}
        {status === "error" && <ErrorView msg={error} onRetry={reset} />}
        {status === "recover" && (
          <RecoverView
            sid={pendingSid}
            rType={rType} setRType={setRType}
            biz={biz} setBiz={setBiz}
            loc={loc} setLoc={setLoc}
            onRecover={(payload) => { setStatus("loading"); runAnalysis(payload, pendingSid); }}
            onReset={reset}
          />
        )}
        {status === "success" && report?.report_type === "quick" && (
          <QuickView report={report} businessType={biz} location={loc} onReset={reset} onUpsell={handleUpsell} copied={copied} onCopy={handleCopy} onPrint={handlePrint} />
        )}
        {status === "success" && (report?.report_type === "full" || report?.report_type === "profiles") && (
          <FullView report={report} businessType={biz} location={loc} onReset={reset} copied={copied} onCopy={handleCopy} onProfilesUpsell={handleProfilesUpsell} />
        )}
        {status === "success" && report?.report_type === "competitor_check" && (
          <CompetitorCheckView report={report} businessType={biz} location={loc} onReset={reset} copied={copied} onCopy={handleCopy} onPrint={handlePrint} />
        )}
      </main>

      <footer className="app-footer">
        Powered by Small Business AI Platform · Data: Google Places, Foursquare
      </footer>
    </div>
  );
}

// ── Input View ────────────────────────────────────────────────────
function RecoverView({ sid, rType, setRType, biz, setBiz, loc, setLoc, onRecover, onReset }) {
  const [errs, setErrs] = useState({});
  const opts = [
    { id: "quick",            label: "Local Market Snapshot — $5" },
    { id: "full",             label: "Local Market Report — $9" },
    { id: "competitor_check", label: "Local Competitor Check — $5" },
  ];
  function handleSubmit(e) {
    e.preventDefault();
    const v = {};
    if (!biz.trim()) v.biz = "Required";
    if (!loc.trim()) v.loc = "Required";
    if (Object.keys(v).length) { setErrs(v); return; }
    onRecover({ business_type: biz.trim(), location: loc.trim(), report_type: rType });
  }
  return (
    <div className="input-view">
      <div className="recover-banner">
        <strong>Payment received — let's get your report.</strong>
        <p>Your browser didn't pass your request through the redirect. Re-enter your details below and we'll generate your report using the payment you already made.</p>
      </div>
      <form className="form-card" onSubmit={handleSubmit} noValidate>
        <div className="fg">
          <label>Report Type</label>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", marginTop: "0.25rem" }}>
            {opts.map(o => (
              <label key={o.id} style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer", fontSize: "0.88rem" }}>
                <input type="radio" name="rtype" value={o.id} checked={rType === o.id} onChange={() => setRType(o.id)} />
                {o.label}
              </label>
            ))}
          </div>
        </div>
        <div className="fg">
          <label htmlFor="rec-biz">Business Type</label>
          <input id="rec-biz" className={errs.biz ? "err" : ""} type="text"
            placeholder="Coffee shop, yoga studio, plumber…"
            value={biz} onChange={e => setBiz(e.target.value)} />
          {errs.biz && <span className="ferr">{errs.biz}</span>}
        </div>
        <div className="fg">
          <label htmlFor="rec-loc">Location</label>
          <input id="rec-loc" className={errs.loc ? "err" : ""} type="text"
            placeholder="Austin, TX"
            value={loc} onChange={e => setLoc(e.target.value)} />
          {errs.loc && <span className="ferr">{errs.loc}</span>}
        </div>
        <button type="submit" className="btn-primary">Generate My Report</button>
        <button type="button" className="btn-sec" onClick={onReset} style={{ marginTop: "0.5rem" }}>Start Over</button>
      </form>
    </div>
  );
}

function InputView({ rType, setRType, biz, setBiz, loc, setLoc, ctx, setCtx, addr, setAddr, errs, onSubmit, onSample }) {
  const [tosAccepted, setTosAccepted] = useState(false);
  const [showTos, setShowTos] = useState(false);
  return (
    <div className="input-view">
      <div className="input-hero">
        <h2>Should you open there?</h2>
        <p>Get a data-backed answer before you invest.</p>
      </div>

      <div className="picker">
        {[
          { id: "quick",            badge: "SNAPSHOT",   price: "$5", name: "Local Market Snapshot",    desc: "Market signal · Opportunity score · Key insights · What to do" },
          { id: "full",             badge: "FULL",       price: "$9", name: "Local Market Report",      desc: "Complete analysis · All competitors · Market dashboard" },
          { id: "competitor_check", badge: "COMPETITOR", price: "$5", name: "Local Competitor Check",   desc: "Top 5 local competitors · Review highlights · Pricing intel · Your edge" },
          { id: "address",          badge: "ADDRESS",    price: "$3", name: "Local Address Check",      desc: "Score a specific address · Foot traffic · Demographics · Go/No-Go", href: "https://location-analyzer-psi.vercel.app" },
        ].map(opt => (
          <button key={opt.id} type="button"
            className={`pick-card ${rType === opt.id ? "active" : ""}`}
            onClick={() => opt.href ? window.open(opt.href, "_blank", "noreferrer") : setRType(opt.id)}>
            <div className={`pick-badge ${opt.id}`}>{opt.badge}</div>
            <div className="pick-price">{opt.price}</div>
            <div className="pick-name">{opt.name}</div>
            <div className="pick-desc">{opt.desc}</div>
            {!opt.href && (
              <span className="pick-sample" onClick={e => { e.stopPropagation(); onSample(opt.id); }}>
                See sample →
              </span>
            )}
            {rType === opt.id && <div className="pick-bar" />}
          </button>
        ))}
      </div>

      <form className="form-card" onSubmit={onSubmit} noValidate>
        <div className="fg">
          <label htmlFor="biz">Business Type</label>
          <input id="biz" className={errs.biz ? "err" : ""} type="text"
            placeholder="Coffee shop, yoga studio, plumber…"
            value={biz} onChange={e => setBiz(e.target.value)} />
          {errs.biz && <span className="ferr">{errs.biz}</span>}
        </div>
        <div className="fg">
          <label htmlFor="loc">Location</label>
          <input id="loc" className={errs.loc ? "err" : ""} type="text"
            placeholder="Austin TX · Downtown Denver · San Jose CA"
            value={loc} onChange={e => setLoc(e.target.value)} />
          {errs.loc && <span className="ferr">{errs.loc}</span>}
        </div>
        {rType === "competitor_check" && (
          <div className="fg">
            <label htmlFor="addr">Your Address <span className="opt">optional</span></label>
            <input id="addr" type="text"
              placeholder="123 Main St — sets you as the center of the search"
              value={addr} onChange={e => setAddr(e.target.value)} />
            <span className="field-hint">Without an address, we use the midpoint of all results found.</span>
          </div>
        )}
        {rType !== "competitor_check" && (
          <div className="fg">
            <label htmlFor="ctx">Additional Context <span className="opt">optional</span></label>
            <textarea id="ctx" rows={2}
              placeholder="Target audience, concept, special focus…"
              value={ctx} onChange={e => setCtx(e.target.value)} />
          </div>
        )}
        <div className="tos-check-row">
          <input type="checkbox" id="tos-checkbox" checked={tosAccepted}
            onChange={e => setTosAccepted(e.target.checked)} />
          <label htmlFor="tos-checkbox">
            I understand this report is for informational purposes only and agree to the{" "}
            <button type="button" className="tos-link" onClick={() => setShowTos(true)}>Terms of Service</button>
          </label>
        </div>
        <button type="submit" className="submit-btn" disabled={!tosAccepted}>
          {rType === "quick" ? "Get Snapshot — $5" : rType === "competitor_check" ? "Get Competitor Check — $5" : "Get Full Report — $9"}
        </button>
        <p className="form-note">One-time payment · No subscription · ~60 seconds</p>
      </form>
      {showTos && <TermsOfService onClose={() => setShowTos(false)} />}
    </div>
  );
}

// ── Competitor Check Result ───────────────────────────────────────
function CompetitorCheckView({ report, businessType, location, onReset, copied, onCopy, onPrint }) {
  const analysis = report.analysis || {};
  const text = analysis.competitor_check_report || "";
  const summary = analysis.summary || {};
  const parsed = parseReport(text);

  return (
    <div className="report-card">
      <div className="card-toolbar">
        <span className="report-tag">Local Competitor Check</span>
        <div className="toolbar-btns">
          <button className="btn-copy" onClick={() => onCopy(text)}>{copied ? "Copied!" : "Copy"}</button>
          <button className="btn-pdf" onClick={onPrint}>Print / Save PDF</button>
          <button className="btn-sec-sm" onClick={onReset}>New</button>
        </div>
      </div>
      {summary.total_found && (
        <div className="check-summary-bar">
          {summary.total_found} competitors within {summary.radius_miles}mi · avg rating {summary.avg_rating}★
        </div>
      )}
      <div className="sections-wrap">
        {parsed?.sections.map((s, i) => <Section key={i} s={s} businessType={businessType} location={location} />)}
        {parsed?.footer.length > 0 && (
          <p className="report-footer">{parsed.footer.join(" · ")}</p>
        )}
      </div>
    </div>
  );
}

// ── Loading ───────────────────────────────────────────────────────
function LoadingView() {
  return (
    <div className="loading-view">
      <div className="spinner" />
      <p className="loading-title">Scanning the market…</p>
      <p className="loading-sub">Google Places + Foursquare · AI analysis · 30–60 seconds</p>
      <p className="loading-note">Don't close this tab</p>
    </div>
  );
}

// ── Error ─────────────────────────────────────────────────────────
function ErrorView({ msg, onRetry }) {
  return (
    <div className="error-view">
      <div className="error-icon">!</div>
      <p className="error-title">Something went wrong</p>
      <p className="error-msg">{msg}</p>
      <button className="btn-sec" onClick={onRetry}>Try Again</button>
    </div>
  );
}

// ── Quick Report ──────────────────────────────────────────────────
function QuickView({ report, businessType, location, onReset, onUpsell, copied, onCopy, onPrint }) {
  const analysis = report.analysis || {};
  const text = analysis.quick_report || "";
  const parsed = parseReport(text);
  const cfg = verdictCfg(parsed?.verdict);

  return (
    <div className="report-card">
      {report._isSample && (
        <div className="sample-banner">Sample report — Coffee Shop · Austin, TX</div>
      )}
      {parsed?.verdict && (
        <div className="verdict-banner" style={{ background: cfg.bg, boxShadow: `0 4px 24px ${cfg.glow}` }}>
          <div className="verdict-left">
            <span className="verdict-label" style={{ color: cfg.text }}>{parsed.verdict}</span>
          </div>
          <div className="verdict-right">
            <span className="verdict-score" style={{ color: cfg.text }}>{parsed.score}</span>
            <span className="verdict-denom" style={{ color: cfg.text }}>/100</span>
          </div>
        </div>
      )}

      <div className="card-toolbar">
        <span className="report-tag">Market Intelligence: Snapshot</span>
        <div className="toolbar-btns">
          <button className="btn-copy" onClick={() => onCopy(text)}>{copied ? "Copied!" : "Copy"}</button>
          <button className="btn-pdf" onClick={onPrint}>Print / Save PDF</button>
          <button className="btn-sec-sm" onClick={onReset}>New</button>
        </div>
      </div>

      <div className="sections-wrap">
        {parsed?.sections.map((s, i) => <Section key={i} s={s} businessType={businessType} location={location} />)}
        {parsed?.footer.length > 0 && (
          <p className="report-footer">{parsed.footer.join(" · ")}</p>
        )}
      </div>

      <div className="upsell-bar">
        <div>
          <strong>Want the full breakdown?</strong>
          <span className="upsell-sub"> All competitors ranked · Market dashboard · Complete analysis</span>
        </div>
        <button className="upsell-btn" onClick={onUpsell}>Full Report — $9 →</button>
      </div>
    </div>
  );
}

const LOCATION_ANALYZER_URL = "https://location-analyzer-psi.vercel.app";

function renderWithCTA(text, businessType, location) {
  // Detect "Analyze an address in [Area] →" pattern
  const ctaMatch = text.match(/^(.*)Analyze an address in (.+?)\s*→\s*(.*)$/i);
  if (ctaMatch) {
    const [, before, area, after] = ctaMatch;
    const fullAddress = location ? `${area}, ${location}` : area;
    const url = `${LOCATION_ANALYZER_URL}?business_type=${encodeURIComponent(businessType || "")}&address=${encodeURIComponent(fullAddress)}`;
    return (
      <span>
        {before}
        <a className="area-cta" href={url} target="_blank" rel="noreferrer">
          Analyze an address in {area} →
        </a>
        {after}
      </span>
    );
  }
  // Detect "→ see full report" teaser lines
  if (text.includes("→ see full report")) {
    const [hint, rest] = text.split("→ see full report");
    return <span>{hint}<span className="teaser-cta">→ see full report for breakdown</span>{rest?.replace("for breakdown","")}</span>;
  }
  return text;
}

// ── Section ───────────────────────────────────────────────────────
function Section({ s, businessType, location }) {
  const isFinal   = /final take/i.test(s.title);
  const isWhereTo = /where to/i.test(s.title);
  return (
    <div className={`section ${isFinal ? "section-final" : ""} ${isWhereTo ? "section-where" : ""}`}>
      <h3 className="section-title">{s.title}</h3>
      {s.bullets.length > 0 && (
        <ul className="section-list">
          {s.bullets.map((b, i) => <li key={i}>{renderWithCTA(b, businessType, location)}</li>)}
        </ul>
      )}
      {s.paragraphs.map((p, i) => {
        const text = typeof p === 'string' ? p : p.text;
        const type = typeof p === 'string' ? 'p' : p.type;
        if (type === 'sub') return <h4 key={i} className="section-subheading">{text}</h4>;
        return <p key={i} className="section-para">{renderWithCTA(text, businessType, location)}</p>;
      })}
    </div>
  );
}

// ── Full Report ───────────────────────────────────────────────────
function FullView({ report, businessType, location, onReset, copied, onCopy, onProfilesUpsell }) {
  const analysis = report.analysis || {};
  const raw = analysis.raw_data || {};
  const metrics = analysis.summary_metrics || {};
  const businesses = raw.businesses || [];
  const compA = raw.competitive_analysis || {};
  const opps = raw.opportunities || {};
  const profiles = analysis.competitor_profiles || null;

  const bizCount = businesses.length || metrics.total_businesses_analyzed || 0;
  const isBundle = report.report_type === "profiles";

  return (
    <div className="report-card">
      {report._isSample && (
        <div className="sample-banner">Sample report — Coffee Shop · Austin, TX</div>
      )}
      <div className="card-toolbar">
        <div className="toolbar-left">
          <span className="report-tag">{isBundle ? "Market Intelligence: Full Report + Competitor Profiles" : "Market Intelligence: Full Report"}</span>
          {bizCount > 0 && (
            <span className="meta-chip">{bizCount} businesses · {metrics.opportunities_identified || 0} opportunities</span>
          )}
        </div>
        <div className="toolbar-btns">
          <button className="btn-copy" onClick={() => onCopy(analysis.comprehensive_report || "")}>{copied ? "Copied!" : "Copy"}</button>
          <button className="btn-pdf" onClick={() => window.print()}>Print / Save PDF</button>
          <button className="btn-sec-sm" onClick={onReset}>New</button>
        </div>
      </div>

      <div className="sections-wrap">
        {(() => {
          const parsed = parseReport(analysis.comprehensive_report || "");
          return parsed?.sections.map((s, i) => <Section key={i} s={s} businessType={businessType} location={location} />) ||
            <pre className="fallback">{analysis.comprehensive_report}</pre>;
        })()}
      </div>

      <div className="report-section-divider">
        <span>Competitor List</span>
      </div>
      <CompTab businesses={businesses} />

      <div className="report-section-divider">
        <span>Market Dashboard</span>
      </div>
      <DashTab
        overview={compA.market_overview || {}}
        leaders={compA.market_leaders || []}
        opportunities={opps.identified_opportunities || []}
        metrics={metrics}
      />

      {profiles ? (
        <>
          <div className="report-section-divider">
            <span>Competitor Profiles</span>
          </div>
          <CompetitorProfiles text={profiles} />
        </>
      ) : !isBundle && !report._isSample && onProfilesUpsell && (
        <div className="profiles-upsell-bar">
          <div className="profiles-upsell-left">
            <strong>Want to know exactly how to beat your top competitors?</strong>
            <span className="profiles-upsell-sub"> Deep-dive profiles: what customers say, how they price, and your specific edge against each one.</span>
          </div>
          <div className="profiles-upsell-cta">
            <button className="profiles-upsell-btn" onClick={onProfilesUpsell}>
              Add Competitor Profiles — $3
            </button>
            <span className="profiles-upsell-note">Your full report will be waiting — competitor profiles will be added below it.</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Competitor Profiles ───────────────────────────────────────────
function CompetitorProfiles({ text }) {
  if (!text) return null;
  const blocks = text.split(/\n(?=COMPETITOR\s)/);
  return (
    <div className="profiles-wrap">
      {blocks.map((block, i) => {
        const lines = block.trim().split("\n");
        const titleLine = lines[0] || "";
        const rest = lines.slice(1);
        const sections = [];
        let cur = null;
        for (const line of rest) {
          const trimmed = line.trim();
          if (!trimmed) continue;
          const isHeader = trimmed === trimmed.toUpperCase() && trimmed.length > 2 && !trimmed.startsWith("-");
          if (isHeader) {
            cur = { title: trimmed, lines: [] };
            sections.push(cur);
          } else if (cur) {
            cur.lines.push(trimmed);
          }
        }
        return (
          <div key={i} className="profile-card">
            <h3 className="profile-title">{titleLine}</h3>
            {sections.map((s, j) => (
              <div key={j} className="profile-section">
                <h4 className="profile-section-title">{s.title}</h4>
                {s.lines.map((l, k) => (
                  l.startsWith("- ")
                    ? <p key={k} className="profile-bullet">• {l.slice(2)}</p>
                    : <p key={k} className="profile-para">{l}</p>
                ))}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

// ── Competitors Tab ───────────────────────────────────────────────
function CompTab({ businesses }) {
  const sorted = [...businesses].sort((a, b) => (b.rating || 0) - (a.rating || 0));
  if (!sorted.length) return <p className="empty">No competitor data available.</p>;

  return (
    <div className="table-wrap">
      <table className="comp-table">
        <thead>
          <tr>
            <th>#</th><th>Business</th><th>Rating</th><th>Reviews</th><th>Type</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((b, i) => {
            const cats = (b.categories || [])
              .filter(c => !["point_of_interest","establishment","food","store","health","locality"].includes(c))
              .slice(0, 2).join(", ") || (b.categories || []).slice(0, 1).join(", ") || "—";
            return (
              <tr key={i} className={i % 2 === 0 ? "r-even" : "r-odd"}>
                <td className="td-rank">{i + 1}</td>
                <td className="td-name">{b.name || "Unknown"}</td>
                <td className="td-rating">
                  <span className="rating-chip" style={{ background: ratingBg(b.rating) }}>
                    {b.rating?.toFixed(1) || "—"}
                  </span>
                </td>
                <td className="td-reviews">{(b.review_count || 0).toLocaleString()}</td>
                <td className="td-cat">{cats}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Dashboard Tab ─────────────────────────────────────────────────
function DashTab({ overview, leaders, opportunities, metrics }) {
  const stats = [
    { label: "Competitors", value: overview.total_competitors || metrics.total_businesses_analyzed || 0 },
    { label: "Avg Rating", value: overview.average_rating ? `${overview.average_rating.toFixed(2)}/5` : "N/A" },
    { label: "Total Reviews", value: (overview.total_reviews || 0).toLocaleString() },
    { label: "Competition", value: overview.competition_level || "Unknown" },
    { label: "Market Quality", value: overview.market_quality || "Unknown" },
  ];

  return (
    <div className="dash-wrap">
      <div className="stat-grid">
        {stats.map((s, i) => (
          <div key={i} className="stat-card">
            <div className="stat-label">{s.label}</div>
            <div className="stat-val">{s.value}</div>
          </div>
        ))}
      </div>

      {leaders.length > 0 && (
        <div className="dash-block">
          <h4 className="dash-title">Top Performers</h4>
          {leaders.slice(0, 5).map((l, i) => (
            <div key={i} className="leader-row">
              <span className="l-rank">{i + 1}</span>
              <span className="l-name">{l.name}</span>
              <span className="rating-chip" style={{ background: ratingBg(l.rating) }}>
                {l.rating?.toFixed(1) || "—"}
              </span>
              <span className="l-reviews">{(l.review_count || 0).toLocaleString()}</span>
              <span className="l-price">{l.price_level || "—"}</span>
            </div>
          ))}
        </div>
      )}

      {opportunities.length > 0 && (
        <div className="dash-block">
          <h4 className="dash-title">Market Gaps</h4>
          {opportunities.slice(0, 6).map((o, i) => (
            <div key={i} className="opp-row">
              <span className={`dot dot-${(o.priority || "").toLowerCase()}`} />
              <div className="opp-body">
                <span className="opp-type">{o.type}</span>
                <span className="opp-desc">{o.description}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
